import PageHeader from '@components/pageHeader';
import PortfolioSelection from '@components/portfolioSelection';
import PortfolioValue from '@components/portfolioValue';
import { ErrorOutline, Moving } from '@mui/icons-material';
import AdapterLuxon from '@mui/lab/AdapterLuxon';
import { Grid, Box, Container, Typography, Stack, IconButton, Popover, Button } from '@mui/material';
import { useAppSelector, useAppDispatch } from '@src/app/hooks/store';
import { BenchmarkInfo } from 'clientportalshared';
import { FC, useEffect, useState } from 'react';
import { BenchmarkCompareCard } from './BenchmarkCompareCard';
import { getPerformanceAsync } from '@src/app/store/reducers/performance/thunks';
import { LocalizationProvider } from '@mui/lab';
import DateRangePicker from '@components/dateRange';
import { DateRange } from '@src/app/store';
import PerformanceChart from './PerformanceChart';
import { selectPerformance } from '@src/app/store/reducers/performance/selectors';
import { PerformanceCard } from './PerformanceCard';
import { ScreenState } from '@src/app/enums';
import { CustomSkeleton } from '@components/index';
import { setPerformanceDateRange } from '@src/app/store/reducers/performance';
import { useAppContext } from '@src/app/hooks/useAppContext';
import { useWindow } from '@src/app/hooks/useWindow';
import { numberFormatterDollar, percentageWithSign, positiveNegativeColor } from '@src/app/utils/GenericUtils';
import { selectPortfolios } from '@src/app/store/reducers/portfolios/selectors';
import { TotalReturnCard } from './TotalReturnCard';
import { setCombining } from '@src/app/store/reducers/portfolios';

const Performance: FC = (): JSX.Element => {
  const [benchmarkSelected, setBenchmarkSelected] = useState<BenchmarkInfo | null>(null);
  const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLElement | null>(null);

  const { portfoliosSelected, portfolioInfo, portfoliosIdsSelected, portfolioInfoState } = useAppSelector(selectPortfolios);
  const { data, state, dateRange } = useAppSelector(selectPerformance);

  const { transitioningMenu } = useAppContext();
  const { resizing } = useWindow();

  const dispatch = useAppDispatch();

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setInfoAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setInfoAnchorEl(null);
  };

  const updateDateRange = (dates: DateRange) => {
    dispatch(setPerformanceDateRange(dates));
  };

  const fetchPerformance = async () => {
    await dispatch(
      getPerformanceAsync({
        compareToBenchmark: benchmarkSelected?.benchmarkKey ?? undefined,
        clientPortfolios: portfoliosSelected,
        fromDate: dateRange.dateFrom,
        toDate: dateRange.dateTo,
      })
    );
  };

  useEffect(() => {
    if (portfoliosIdsSelected.length > 1) {
      dispatch(setCombining(true));
    }
  }, []);

  useEffect(() => {
    fetchPerformance();
  }, [benchmarkSelected, dateRange, portfoliosSelected]);

  const incomeReturnValue = data?.incomeReturnValue ?? 0;
  const incomeReturnPercentage = data?.incomeReturnPercentage ?? 0;
  const capitalReturnValue = data?.capitalReturnValue ?? 0;
  const capitalReturnPercentage = data?.capitalReturnPercentage ?? 0;
  const totalReturnValue = data?.totalReturnValue ?? 0;
  const totalReturnPercentage = data?.totalReturnPercentage ?? 0;
  const benchmarks = portfolioInfo.benchMarkInfoList || [];
  return (
    <Container>
      <Box display="flex" flexDirection="column" gap={4} mb={6}>
        <PageHeader
          title="Performance"
          icon={Moving}
          titleAction={
            <IconButton aria-haspopup="true" onClick={handlePopoverOpen}>
              <ErrorOutline sx={{ color: 'black' }} />
            </IconButton>
          }
        >
          <PortfolioSelection allowCombining={true} disabled={state === ScreenState.Loading || portfolioInfoState === ScreenState.Loading} />
        </PageHeader>
        <Grid container direction="column" spacing={4}>
          <Grid item>
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <DateRangePicker dateRange={dateRange} setDateRange={updateDateRange} />
            </LocalizationProvider>
          </Grid>
          <Grid item>
            <Box
              display="flex"
              sx={{ flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'normal', md: 'center' } }}
              justifyContent="space-between"
              gap={2}
            >
              <PortfolioValue totalValue={data?.totalValue ?? 0} />
              <Box display="flex" gap={2}>
                <Stack direction="column" flex={1}>
                  <Typography noWrap fontWeight={700}>
                    Capital Return
                  </Typography>
                  <Typography noWrap fontWeight={700}>
                    Income Return
                  </Typography>
                  <Typography noWrap fontWeight={700}>
                    Total Return
                  </Typography>
                </Stack>
                <Stack direction="column" flex={1}>
                  <Box display="flex" gap={1}>
                    <Typography noWrap fontWeight={700} sx={{ color: positiveNegativeColor(capitalReturnValue) }}>
                      {numberFormatterDollar(capitalReturnValue)}
                    </Typography>
                    <Typography noWrap fontWeight={700} sx={{ color: positiveNegativeColor(capitalReturnPercentage) }}>
                      {`(${percentageWithSign(capitalReturnPercentage)})`}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1}>
                    <Typography noWrap fontWeight={700} sx={{ color: positiveNegativeColor(incomeReturnValue) }}>
                      {numberFormatterDollar(incomeReturnValue)}
                    </Typography>
                    <Typography noWrap fontWeight={700} sx={{ color: positiveNegativeColor(incomeReturnPercentage) }}>
                      {`(${percentageWithSign(incomeReturnPercentage)})`}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1}>
                    <Typography noWrap fontWeight={700} sx={{ color: positiveNegativeColor(totalReturnValue) }}>
                      {numberFormatterDollar(totalReturnValue)}
                    </Typography>
                    <Typography noWrap fontWeight={700} sx={{ color: positiveNegativeColor(totalReturnPercentage) }}>
                      {`(${percentageWithSign(totalReturnPercentage)})`}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Grid>
          <Grid item container spacing={2}>
            {portfoliosSelected.length > 0 && (
              <Grid item xs={12} sm={12} lg={6}>
                <TotalReturnCard dateRange={dateRange} data={data} />
              </Grid>
            )}
            {portfoliosSelected.length > 0 && benchmarks.length > 0 && (
              <Grid item xs={12} sm={12} lg={6}>
                <BenchmarkCompareCard
                  benchmarkSelected={benchmarkSelected}
                  setBenchmarkSelected={setBenchmarkSelected}
                  benchmarks={benchmarks}
                  data={data}
                />
              </Grid>
            )}
          </Grid>
          <Grid item>
            {resizing || transitioningMenu || state === ScreenState.Loading || portfolioInfoState === ScreenState.Loading ? (
              <CustomSkeleton variant="rectangular" height={250} numberOfItems={1} />
            ) : (
              <PerformanceChart performance={data} />
            )}
          </Grid>
          <Grid item>
            <Box display="flex" justifyContent="center" zIndex={2}>
              <Grid container spacing={2}>
                {data?.portfolioInfoDetails
                  ?.filter((detail) => detail.id && portfoliosIdsSelected.includes(detail.id))
                  .map((detail, index) => (
                    <Grid key={index} item xs={12} xl={6}>
                      <PerformanceCard
                        name={detail.name ?? ''}
                        totalValue={detail.totalValue ?? 0}
                        capitalReturnValue={detail.capitalReturnValue ?? 0}
                        capitalReturnPercentage={detail.capitalReturnPercentage ?? 0}
                        incomeReturnValue={detail.incomeReturnValue ?? 0}
                        incomeReturnPercentage={detail.incomeReturnPercentage ?? 0}
                        totalReturnPercentage={detail.totalReturnPercentage ?? 0}
                        totalReturnValue={detail.totalReturnValue ?? 0}
                        portfolioKey={portfoliosSelected.find((x) => x.portfolioId === detail.id)?.portfolioKey ?? ''}
                      />
                    </Grid>
                  ))}
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Popover
        anchorEl={infoAnchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableRestoreFocus
        open={Boolean(infoAnchorEl)}
      >
        <Box display="flex" flexDirection="column" padding={2} maxWidth="600px">
          <Typography>
            Your portfolio performance is calculated using a daily compounded Time Weighted Rate of Return (TWRR) methodology. The TWRR methodology
            breaks up the return on an investment portfolio into daily intervals. The TWRR provides the rate of return for each day. The TWRR is
            calculated by multiplying each day&apos;s rate of return by each other. By isolating daily returns, the result is more accurate than
            simply taking the beginning balance and ending balance of the time invested.
          </Typography>
          <Box display="flex" justifyContent="flex-end">
            <Button onClick={handlePopoverClose} variant="text">
              Ok
            </Button>
          </Box>
        </Box>
      </Popover>
    </Container>
  );
};

export default Performance;
